import { AlertTriangle, CalendarClock, Globe2, Info } from "lucide-react";
import { IST_TZ } from "../../lib/timeConverter";

const to12 = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`; };

function TimeCard({ title, d, place, accent, testId }) {
    return (
        <div className={`rounded-xl p-4 border ${accent ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-white border-slate-200 text-[#0F172A]"}`} data-testid={testId}>
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accent ? "text-blue-200" : "text-slate-500"}`}>{title}</div>
            <div className="font-heading font-bold text-3xl sm:text-4xl mt-1 leading-tight" data-testid={`${testId}-time`}>{d.time}</div>
            <div className="text-sm font-semibold mt-1" data-testid={`${testId}-date`}>{d.date}</div>
            <div className={`text-xs mt-2 break-words ${accent ? "text-blue-100" : "text-slate-500"}`} data-testid={`${testId}-zone`}>
                {place} · {d.abbr ? `${d.abbr} · ` : ""}{d.offset}
            </div>
        </div>
    );
}

function Notice({ tone, icon: Icon, title, children, testId }) {
    const cls = tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-700";
    return (
        <div className={`rounded-xl border p-4 text-sm ${cls}`} data-testid={testId} role="status">
            <div className="flex items-start gap-2">
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="min-w-0"><div className="font-bold">{title}</div><div className="mt-1 leading-relaxed">{children}</div></div>
            </div>
        </div>
    );
}

export function ConverterResult({ c, prefix }) {
    const r = c.result;
    if (r.status === "no-zone") {
        return <Notice icon={Globe2} title="Choose a location" testId={`${prefix}-result-empty`}>Search for a country, city or time zone to see the converted time.</Notice>;
    }
    if (r.status === "invalid") {
        return <Notice tone="warn" icon={AlertTriangle} title="Please check the inputs" testId={`${prefix}-result-invalid`}>The date, time or time zone isn't valid. Pick them again from the fields.</Notice>;
    }
    if (r.status === "nonexistent") {
        return (
            <Notice tone="warn" icon={AlertTriangle} title="This local time doesn't exist" testId={`${prefix}-result-nonexistent`}>
                Clocks in this time zone jump forward for daylight saving on this date, so {c.time} never happens there.
                {r.suggestion && (
                    <button type="button" onClick={() => c.set({ time: r.suggestion })} data-testid={`${prefix}-use-suggestion`}
                        className="mt-2 block font-bold text-[#1E3A8A] underline underline-offset-2">Use {c.hour12 ? to12(r.suggestion) : r.suggestion} instead</button>
                )}
            </Notice>
        );
    }
    if (r.status === "ambiguous") {
        return (
            <Notice tone="warn" icon={AlertTriangle} title="This local time happens twice" testId={`${prefix}-result-ambiguous`}>
                Clocks fall back for daylight saving on this date. Which one does the parent mean?
                <div className="mt-2 flex flex-wrap gap-2">
                    {r.options.map((o, i) => (
                        <button key={o.offset} type="button" onClick={() => c.set({ pick: i })} data-testid={`${prefix}-ambiguous-pick-${i}`}
                            className="px-3 py-2 rounded-lg bg-white border border-amber-300 text-xs font-bold text-amber-900 hover:border-amber-500">
                            {i === 0 ? "First" : "Second"} {o.time} ({o.abbr || o.offset})
                        </button>
                    ))}
                </div>
            </Notice>
        );
    }
    const srcPlace = r.source.tz === IST_TZ ? "India" : c.place;
    const dstPlace = r.dest.tz === IST_TZ ? "India" : c.place;
    const shift = r.dayShift.diff;
    return (
        <div className="space-y-3" data-testid={`${prefix}-result`} aria-live="polite">
            <TimeCard title={r.source.tz === IST_TZ ? "Source · India (IST)" : "Source · Local time"} d={r.source} place={srcPlace} testId={`${prefix}-source`} />
            <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-slate-400" />
                <span data-testid={`${prefix}-day-shift`}
                    className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${shift === 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                    {r.dayShift.label}
                </span>
            </div>
            <TimeCard title={r.dest.tz === IST_TZ ? "Converted · India (IST)" : "Converted · Local time"} d={r.dest} place={dstPlace} accent testId={`${prefix}-dest`} />
            <p className="text-[11px] text-slate-500 flex items-start gap-1.5" data-testid={`${prefix}-disclaimer`}>
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Calculated time only — this is not a confirmed demo booking. Our team confirms every class separately.
            </p>
        </div>
    );
}
